import React, { createContext, useMemo } from 'react';
import {
  LoadableComponentContextProvider,
  UseLoadableComponentOptions,
} from '@wix/da-hooks/pkg/useLoadableComponent';
import { Editor } from '@tiptap/core';
import { EditorContextProviderWrapper } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import { CommentableTypes } from '../Comment/redux/types';
import { CommentEditorSource } from './redux/types';
import {
  useCommentEditorSpecifics,
  UseCommentEditorOptions,
  UseCommentEditorValue,
} from './hooks/useCommentEditorSpecifics';
import DraftCommentAutoTrigger from './DraftCommentAutoTrigger';

interface CommentEditorContextOptions extends UseCommentEditorOptions {
  commentBoxId: string;
  commentableType: CommentableTypes;
  commentableItemId: string | number;
  submitValidator?: (state: Editor) => boolean;
  children?: React.ReactNode | undefined;
}

interface CommentEditorContextValue extends UseCommentEditorValue {
  editorId: string;
  commentableType: CommentableTypes;
  commentableItemId: string | number;
  submitValidator?: (state: Editor) => boolean;
}

export const CommentEditorContext = createContext<CommentEditorContextValue>({
  // Mostly gibberish defaults
  editorId: '',
  commentableItemId: '',
  commentableType: CommentableTypes.COMMENT_USER,
  isBusy: false,
  shouldResetEditor: false,
  shouldFocus: false,
  editorSource: CommentEditorSource.Independent,
  isExpanded: false,
  expandEditor: () => null,
  collapseEditor: () => null,
});

const CommentEditorContextProvider: React.FC<CommentEditorContextOptions> = ({
  children,
  commentBoxId,
  commentableItemId,
  commentableType,
  submitValidator,
  ...useCommentEditorOptions
}) => {
  const {
    editorSource,
    isBusy,
    shouldResetEditor,
    relevantComment,
    shouldFocus,
    isExpanded,
    expandEditor,
    collapseEditor,
    rootElementRef,
    initialContent,
  } = useCommentEditorSpecifics(
    commentBoxId,
    commentableItemId,
    commentableType,
    useCommentEditorOptions
  );

  const memoizedCommentEditorContext = useMemo(
    () => ({
      editorSource,
      isBusy,
      shouldResetEditor,
      relevantComment,
      shouldFocus,
      isExpanded,
      expandEditor,
      collapseEditor,
      initialContent,
      rootElementRef,
      editorId: commentBoxId,
      commentableItemId,
      commentableType,
    }),
    [
      editorSource,
      isBusy,
      shouldResetEditor,
      relevantComment,
      shouldFocus,
      isExpanded,
      expandEditor,
      collapseEditor,
      initialContent,
      rootElementRef,
      commentableItemId,
      commentableType,
      commentBoxId,
    ]
  );

  return (
    <CommentEditorContext.Provider value={memoizedCommentEditorContext}>
      {children}
    </CommentEditorContext.Provider>
  );
};

export interface CommentEditorProviderProps
  extends Omit<CommentEditorContextOptions, 'isSubmittable'>,
    UseLoadableComponentOptions {
  submitValidator?: (newState: Editor) => boolean;
  children?: React.ReactNode | undefined;
}

/**
 * Contains all the context providers we need for a comment editor implementation.
 *
 * Includes: `EditorContext`, `LoadableComponentContext`, and `CommentEditorContext`.
 */
export const CommentEditorProviders: React.FC<CommentEditorProviderProps> = ({
  children,
  forceLoadOnMount,
  commentBoxId,
  submitValidator,
  ...rest
}) => {
  return (
    <EditorContextProviderWrapper>
      <LoadableComponentContextProvider forceLoadOnMount={forceLoadOnMount}>
        <CommentEditorContextProvider
          commentBoxId={commentBoxId}
          submitValidator={submitValidator}
          {...rest}
        >
          <DraftCommentAutoTrigger editorId={commentBoxId} />
          {children}
        </CommentEditorContextProvider>
      </LoadableComponentContextProvider>
    </EditorContextProviderWrapper>
  );
};
